import React from "react"
import { graphql, useStaticQuery } from "gatsby"
import "./style.css"
import { FooterWrapper } from "./footer.style"
import footerData from "../../../content/data/footer.yml"

import Icon from "react-icons-kit"
import { facebook } from "react-icons-kit/fa/facebook"
import { instagram } from "react-icons-kit/fa/instagram"
import { linkedin } from "react-icons-kit/fa/linkedin"
import { whatsapp } from "react-icons-kit/fa/whatsapp"
import { BsTwitterX } from "react-icons/bs"
import { behance } from "react-icons-kit/fa/behance"

import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from "react-icons/fa"
import useLang from "../../hooks/useLang"
import useWebsiteCmsContent from "../../hooks/useWebsiteCmsContent"
import { blockContent, localizedBlockData } from "../../services/websiteCms"

const ListItem = ({ label, icon, link }) => {
  if (!link) return null
  return (
    <li className="footer-social-item">
      <a
        rel="noopener noreferrer"
        href={link}
        aria-label={label}
        title={label}
        target="_blank"
        className="footer-social-link"
      >
        <Icon icon={icon} className="footer-social-icon" />
      </a>
    </li>
  )
}

const splitTwoLines = (text = "") => {
  if (!text) return { line1: "", line2: "" }
  const parts = String(text)
    .split(",")
    .map(s => s.trim())
    .filter(Boolean)

  if (parts.length <= 2) return { line1: text, line2: "" }
  return {
    line1: parts.slice(0, 2).join(", "),
    line2: parts.slice(2).join(", "),
  }
}

const formatPhoneNumber = (phone = "") => {
  if (!phone) return ""
  const cleanPhone = String(phone).trim()
  if (cleanPhone.startsWith("+")) return cleanPhone
  if (cleanPhone.endsWith("+")) return `+${cleanPhone.slice(0, -1).trim()}`
  return cleanPhone
}

const Footer = () => {
  const lang = useLang()
  const isAr = lang === "ar"
  const cmsPayload = useWebsiteCmsContent()
  const cmsFooter = blockContent(cmsPayload, "footer")
  const activeFooterData = cmsFooter && typeof cmsFooter === "object" ? cmsFooter : footerData
  const cmsContact = localizedBlockData(cmsPayload, "contact-page", lang)

  const data = useStaticQuery(graphql`
    query FooterContactData {
      enContact: allMarkdownRemark(
        limit: 1
        filter: { frontmatter: { templateName: { eq: "contact" }, lang: { eq: "en" } } }
      ) {
        nodes {
          frontmatter {
            addressTitle
            addressText
            phone
            phoneList { title link }
            emailTitle
            emailLink
            workingHourse
            workingHourseText
          }
        }
      }
      arContact: allMarkdownRemark(
        limit: 1
        filter: { frontmatter: { templateName: { eq: "contact" }, lang: { eq: "ar" } } }
      ) {
        nodes {
          frontmatter {
            addressTitle
            addressText
            phone
            phoneList { title link }
            emailTitle
            emailLink
            workingHourse
            workingHourseText
          }
        }
      }
    }
  `)

  const staticContact = (isAr ? data?.arContact?.nodes?.[0] : data?.enContact?.nodes?.[0])?.frontmatter || {}
  const contact = cmsContact || staticContact
  const address = splitTwoLines(contact.addressText)
  const phones = contact.phoneList || []
  const primaryPhone = formatPhoneNumber(phones[0]?.title || "")
  const secondaryPhones = phones.slice(1).map(p => formatPhoneNumber(p?.title)).filter(Boolean).join(" / ")

  let socialBlock = null
  let workingHoursText = ""

  const activeFooterColumns = Array.isArray(activeFooterData?.columns)
    ? activeFooterData.columns
    : []

  activeFooterColumns.forEach((column) => {
    if (!column.menu) {
      workingHoursText = column.workingHours?.textObj?.[lang] || ""
      socialBlock = (
        <ul className="footer-social-squares">
          <ListItem label="Facebook" icon={facebook} link={column.social?.facebook} />
          {!!column.social?.twitter && (
            <li className="footer-social-item">
              <a rel="noopener noreferrer" href={column.social.twitter} aria-label="Twitter" title="Twitter" target="_blank" className="footer-social-link">
                <BsTwitterX className="footer-social-icon" />
              </a>
            </li>
          )}
          <ListItem label="Instagram" icon={instagram} link={column.social?.instagram} />
          <ListItem label="LinkedIn" icon={linkedin} link={column.social?.linkedin} />
          <ListItem label="WhatsApp" icon={whatsapp} link={column.social?.whatsapp} />
          <ListItem label="Behance" icon={behance} link={column.social?.behance} />
        </ul>
      )
    }
  })

  return (
    <FooterWrapper dir={isAr ? "rtl" : "ltr"} className="footer-root">
      <div className="container footer-container">
        <div className="footer-content-grid">
          
          {/* LEFT: Working Hours + Social */}
          <div className="footer-left-section">
            <div className="footer-working-hours">
              <div className="footer-icon-wrapper">
                <FaClock className="footer-icon-clock" />
              </div>
              <div className="footer-working-content">
                <span className="footer-working-title">{isAr ? "ساعات العمل" : "Working Hours"}</span>
                <p className="footer-working-text">{workingHoursText}</p>
              </div>
            </div>

            {socialBlock && <div className="footer-social-section">{socialBlock}</div>}
          </div>

          {/* RIGHT: Contact Info */}
          <div className="footer-right-section">
            <ul className="footer-contact">
              
              {/* Address */}
              <li className="footer-contact-item">
                <div className="footer-contact-icon-wrapper">
                  <FaMapMarkerAlt className="footer-contact-icon" />
                </div>
                <div className="footer-contact-body">
                  <div className="footer-contact-line1">{address.line1 || contact.addressTitle || ""}</div>
                  {address.line2 && <div className="footer-contact-line2">{address.line2}</div>}
                </div>
              </li>

              {/* Phone */}
              <li className="footer-contact-item">
                <div className="footer-contact-icon-wrapper">
                  <FaPhoneAlt className="footer-contact-icon" />
                </div>
                <div className="footer-contact-body">
                  {primaryPhone && (
                    <a href={`tel:${primaryPhone.replace(/\s+/g, '')}`} className="footer-contact-phone">
                      {primaryPhone}
                    </a>
                  )}
                  {secondaryPhones && <div className="footer-contact-sub">{secondaryPhones}</div>}
                </div>
              </li>

              {/* Email */}
              <li className="footer-contact-item">
                <div className="footer-contact-icon-wrapper">
                  <FaEnvelope className="footer-contact-icon" />
                </div>
                <div className="footer-contact-body">
                  {contact.emailLink && (
                    <a href={`mailto:${contact.emailLink}`} className="footer-contact-email">
                      {contact.emailLink}
                    </a>
                  )}
                </div>
              </li>

            </ul>
          </div>

        </div>

        {/* Bottom Credit */}
        <div className="footer-credit">
          <div className="footer-credit-bar" />
          <p className="footer-credit-text">
            {activeFooterData.copy?.[lang]}<sup>®</sup>
          </p>
        </div>
      </div>
    </FooterWrapper>
  )
}

export default Footer
